import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '../Logo';
import { UnifiedMobileMenu } from './UnifiedMobileMenu';
import { UserDropdown } from './UserDropdown';
import { useSession } from '@supabase/auth-helpers-react';
import { getMainNavigationItems } from '@/data/navigationData';

const ResponsiveHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();

  const isAuthPage = location.pathname === '/auth';
  const mainNavigationItems = getMainNavigationItems(!!session);
  
  if (isAuthPage) return null;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/98 backdrop-blur-md border-b border-border shadow-sm">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Logo />
          </div>

          {/* Search Bar - Large Desktop Only */}
          <div className="hidden xl:block flex-1 max-w-md mx-8">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search tools and features..."
                className="w-full pl-10 pr-4 py-2.5 bg-muted/30 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {/* Desktop Navigation - XL screens and up */}
          <nav className="hidden xl:flex items-center gap-6 mx-8">
            {mainNavigationItems.map((item) => (
              <Link 
                key={item.id}
                to={item.path} 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {session ? (
              <>
                {/* Notifications - Large screens only */}
                <div className="hidden lg:block">
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground transition-all duration-200 relative">
                    <Bell className="h-5 w-5" />
                    <div className="absolute top-1 right-1 h-2 w-2 bg-destructive rounded-full animate-pulse"></div>
                  </Button>
                </div>
                <UserDropdown />
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/auth')}
                  className="text-muted-foreground hover:text-foreground transition-all duration-200"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* Mobile/Tablet Menu Toggle - XL screens and below */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden text-muted-foreground hover:text-foreground transition-all duration-200"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Unified Mobile Menu */}
      <UnifiedMobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
        session={session} 
      />
    </header>
  );
};

export default ResponsiveHeader;